import { makeAutoObservable, runInAction } from 'mobx'
import pMap from 'p-map'
import { v4 } from 'uuid'
import { visorPRORestClient } from '../util/api'
import type { Stores } from '../util/store'
import { DictionaryArray } from '../types'
import { type DocumentUpload } from '../types'
import { useMemo } from 'react'

const MAX_CONCURRENT_UPLOADS = 2

export class DocumentUploadStore {
    public documentUploadsById: Record<string, DocumentUpload> = {}

    constructor(private readonly stores: Stores) {
        makeAutoObservable(this)
    }

    public getDataSetUploads(dataSetId: string) {
        return Object.values(this.documentUploadsById).filter(
            (upload) => upload.data_set_id === dataSetId,
        )
    }

    public async enqueueDocumentUploads(dataSetId: string, files: FileList) {
        const uploads = Array.from(files).map((file) => {
            const upload: DocumentUpload = {
                id: v4(),
                file,
                data_set_id: dataSetId,
                progress: 0,
                status: 'pending',
            }
            return upload
        })

        runInAction(() => {
            uploads.forEach((upload) => {
                this.documentUploadsById[upload.id] = upload
            })
        })

        await pMap(uploads, (upload) => this.sendUploadRequest(upload), {
            concurrency: MAX_CONCURRENT_UPLOADS,
        })
    }

    private async sendUploadRequest(upload: DocumentUpload) {
        runInAction(() => {
            this.documentUploadsById[upload.id] = {
                ...upload,
                status: 'uploading',
            }
        })

        try {
            const document = await visorPRORestClient.document.upload(
                upload.file,
                upload.data_set_id,
            )

            runInAction(() => {
                delete this.documentUploadsById[upload.id]

                if (upload.data_set_id) {
                    const dataSetId = upload.data_set_id
                    const dataSets = this.stores.dataSets.dataSetsById
                    const oldDataSet = dataSets[dataSetId]
                    const newDataSet = {
                        [document.id]: document,
                        ...oldDataSet,
                    }
                    dataSets[dataSetId] = newDataSet
                }
            })
        } catch (e) {
            runInAction(() => {
                this.documentUploadsById[upload.id] = {
                    ...upload,
                    status: 'error',
                }
            })
        }
    }
}

export const useDocumentUploads = (stores: Stores) => {
    return useMemo(() => {
        return new DictionaryArray(stores.documentUploads.documentUploadsById)
    }, [stores.documentUploads.documentUploadsById])
}
